// umlaut-check: skip — Funktionsnamen ("formatKw", "sicherheitLabel" usw.)
// sind technische Bezeichner; sichtbare Rückgabetexte tragen Umlaute korrekt.

import {useEffect, useState} from "react";

const zahlFormatter = new Intl.NumberFormat("de-DE", {maximumFractionDigits: 0});

/** Stückzahlen der Cosmos-Seiten: gerundet, mit deutschem Tausenderpunkt. */
export function formatMenge(wert) {
    if (wert === null || wert === undefined || Number.isNaN(Number(wert))) {
        return "–";
    }
    return zahlFormatter.format(Math.round(Number(wert)));
}

/** "2026-08-18T08:21:33+02:00" -> "18.08.2026 08:21". */
export function formatDatumZeit(iso) {
    if (!iso) {
        return null;
    }
    const datum = new Date(iso);
    if (Number.isNaN(datum.getTime())) {
        return String(iso);
    }
    return datum.toLocaleString("de-DE", {day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"});
}

/** "2026-08-18T..." bzw. "2026-08-18" -> "18.08.2026". */
export function formatDatum(iso) {
    if (!iso) {
        return null;
    }
    const datum = new Date(iso);
    if (Number.isNaN(datum.getTime())) {
        return String(iso);
    }
    return datum.toLocaleDateString("de-DE", {day: "2-digit", month: "2-digit", year: "numeric"});
}

/** Cosmos-eigene Wochenbezeichnung aus der Ampel-Datei ("CW38", "CW40-42") -> "KW 38"/"KW 40-42". */
export function formatCosmosWoche(bezeichnung) {
    if (!bezeichnung) {
        return "–";
    }
    return String(bezeichnung).replace(/^CW\s*/i, "KW ");
}

/** "2026-39" -> "KW 39/26". */
export function formatKw(kw) {
    if (!kw) {
        return "–";
    }
    const [jahr, woche] = String(kw).split("-");
    if (!jahr || !woche) {
        return kw;
    }
    return `KW ${woche}/${String(jahr).slice(2)}`;
}

export const SICHERHEIT_LABEL = {
    ist: "ist",
    gemessen: "gemessen",
    gepflegt: "gepflegt",
    errechnet: "errechnet",
    angenommen: "angenommen",
    aus_datei: "aus Datei",
};

export function sicherheitLabel(sicherheit) {
    return SICHERHEIT_LABEL[sicherheit] || sicherheit;
}

/** Baut den Tooltip-Text aus einem {wert,quelle,sicherheit}-Feld (Prognose-Rückgabeform). */
export function herkunftText(feld) {
    if (!feld || !feld.quelle) {
        return null;
    }
    return feld.sicherheit ? `${feld.quelle} · ${sicherheitLabel(feld.sicherheit)}` : feld.quelle;
}

/** "2026-39" -> Datum des Montags dieser ISO-Woche (lokale Zeit, keine UTC-Verschiebung). */
export function kwZuMontag(kw) {
    if (!kw) {
        return null;
    }
    const [jahrTxt, wocheTxt] = String(kw).split("-");
    const jahr = Number(jahrTxt);
    const woche = Number(wocheTxt);
    if (!Number.isFinite(jahr) || !Number.isFinite(woche)) {
        return null;
    }
    // ISO-8601: der 4. Januar liegt immer in KW 1.
    const vierterJanuar = new Date(jahr, 0, 4);
    const montagKw1 = new Date(vierterJanuar);
    const wochentag = (vierterJanuar.getDay() + 6) % 7; // 0 = Montag
    montagKw1.setDate(vierterJanuar.getDate() - wochentag);
    const montag = new Date(montagKw1);
    montag.setDate(montagKw1.getDate() + (woche - 1) * 7);
    return montag;
}

/** Datum -> ISO-Kalenderwoche im Cosmos-Format ("2026-39"), für den Zeitstrahl-Marker "aktuelle Woche". */
export function isoWocheVon(datum) {
    const bezugstag = new Date(Date.UTC(datum.getFullYear(), datum.getMonth(), datum.getDate()));
    const wochentag = (bezugstag.getUTCDay() + 6) % 7; // 0 = Montag
    bezugstag.setUTCDate(bezugstag.getUTCDate() - wochentag + 3); // Donnerstag derselben ISO-Woche
    const jahresstart = new Date(Date.UTC(bezugstag.getUTCFullYear(), 0, 1));
    const woche = Math.ceil((((bezugstag - jahresstart) / 86400000) + 1) / 7);
    return `${bezugstag.getUTCFullYear()}-${String(woche).padStart(2, "0")}`;
}

/** Heutige Kalenderwoche im Cosmos-Format - für die Markierung "aktuelle Woche" im Zeitstrahl. */
export function heutigeKw() {
    return isoWocheVon(new Date());
}

/** "2026-39" -> Datum des Sonntags derselben ISO-Woche. */
export function kwZuSonntag(kw) {
    const montag = kwZuMontag(kw);
    if (!montag) {
        return null;
    }
    const sonntag = new Date(montag);
    sonntag.setDate(montag.getDate() + 6);
    return sonntag;
}

/** Date -> "2026-09-19" (ohne Zeitzonen-Verschiebung durch toISOString). */
export function alsIsoDatum(datum) {
    if (!datum) {
        return null;
    }
    const jahr = datum.getFullYear();
    const monat = String(datum.getMonth() + 1).padStart(2, "0");
    const tag = String(datum.getDate()).padStart(2, "0");
    return `${jahr}-${monat}-${tag}`;
}

/** "2026-09-19" -> "19.09." (kurzes Datum für Achsenbeschriftungen). */
export function formatDatumKurz(iso) {
    if (!iso) {
        return "";
    }
    const [, monat, tag] = String(iso).split("-");
    return `${tag}.${monat}.`;
}

/** Dringlichkeit eines Bestellvorschlags (AP9, PLAN.md Abschnitt 4.7). */
export const DRINGLICHKEIT_LABEL = {
    ueberfaellig: "Überfällig",
    sofort: "Sofort",
    bald: "Bald",
    geplant: "Geplant",
};

export const DRINGLICHKEIT_BADGE = {
    ueberfaellig: "text-bg-danger",
    sofort: "text-bg-warning",
    bald: "text-bg-info",
    geplant: "text-bg-secondary",
};

export function dringlichkeitLabel(dringlichkeit) {
    return DRINGLICHKEIT_LABEL[dringlichkeit] || dringlichkeit || "–";
}

/** Lebenszyklus-Status eines Bestellvorschlags (offen|uebernommen|verworfen). */
export const STATUS_LABEL = {
    offen: "Offen",
    uebernommen: "Übernommen",
    verworfen: "Verworfen",
};

export const STATUS_BADGE = {
    offen: "text-bg-light border",
    uebernommen: "text-bg-success",
    verworfen: "text-bg-secondary",
};

export function statusLabel(status) {
    return STATUS_LABEL[status] || status || "–";
}

/** Status einer Lieferposition (cosmos_lieferpositionen.status). */
export const LIEFERPOSITION_STATUS_LABEL = {
    geplant: "Geplant",
    bestaetigt: "Bestätigt",
    kommissionierung: "Kommissionierung",
    teilgeliefert: "Teilgeliefert",
    geliefert: "Geliefert",
    verschoben: "Verschoben",
};

export function lieferpositionStatusLabel(status) {
    return LIEFERPOSITION_STATUS_LABEL[status] || status || "–";
}

/** Prozentzahlen der Cosmos-Seiten: eine Nachkommastelle, deutsches Komma, Leerzeichen vor "%". */
export function formatProzent(wert, nachkommastellen = 1) {
    if (wert === null || wert === undefined || Number.isNaN(Number(wert))) {
        return "–";
    }
    return `${new Intl.NumberFormat("de-DE", {minimumFractionDigits: nachkommastellen, maximumFractionDigits: nachkommastellen}).format(Number(wert))} %`;
}

/** Saisonfaktoren u. Ä.: deutsches Komma statt Punkt, ohne Einheit. */
export function formatFaktor(wert, nachkommastellen = 2) {
    if (wert === null || wert === undefined || Number.isNaN(Number(wert))) {
        return "–";
    }
    return new Intl.NumberFormat("de-DE", {minimumFractionDigits: nachkommastellen, maximumFractionDigits: nachkommastellen}).format(Number(wert));
}

/** Farbnummer dreistellig mit führenden Nullen: "16" -> "#016", mit Unternummer "#016-1". */
export function formatFarbnummer(nummer, unter) {
    if (nummer === null || nummer === undefined || nummer === "") {
        return "";
    }
    const basis = String(nummer).padStart(3, "0");
    return `#${basis}${unter ? "-" + unter : ""}`;
}

/** "1" / "0" / true / false -> "Ja" / "Nein" (Rückfall für ohne "eingabe"-Angabe gepflegte Ja/Nein-Einstellungen). */
export function jaNeinLabel(wert) {
    if (wert === null || wert === undefined || wert === "") {
        return "–";
    }
    return Number(wert) ? "Ja" : "Nein";
}

/** Die vier Produktlinien (cosmos_artikel_parameter.produktlinie) - zentrale Stelle statt Kopien je Datei. */
export const PRODUKTLINIE_LABEL = {
    PREMIUM: "PREMIUM",
    FLAME_ORANGE: "FLAME Orange",
    FLAME_BLUE: "FLAME Blue",
    BURNER: "BURNER",
};

export function produktlinieLabel(produktlinie) {
    return PRODUKTLINIE_LABEL[produktlinie] || produktlinie || "–";
}

/** Marken (cosmos_lieferungen.marke, aus der Ampel-Datei) - die Marken heißen MOLOTOW und BURNER und FLAME (Befund Sichtprüfung 3 #S19). */
export const MARKE_LABEL = {
    Molotow: "MOLOTOW",
    Burner: "BURNER",
    Flame: "FLAME",
};

export function markeLabel(marke) {
    return MARKE_LABEL[marke] || marke || "–";
}

/** Lager je Weclapp-ID (KONVENTIONEN.md: SNV = Schneider, Lager Weilheim, Weclapp-ID 8034). */
export const LAGER_LABEL = {
    8034: "SNV Weilheim",
    3827: "Lahr",
};

export function lagerLabel(lagerId) {
    if (lagerId === null || lagerId === undefined || lagerId === "") {
        return "–";
    }
    return LAGER_LABEL[lagerId] || LAGER_LABEL[Number(lagerId)] || `Lager ${lagerId}`;
}

/** Produktionsstatus aus der Ampel-Datei (cosmos_produktionsstaende.status) - Rückfall, solange `status_text` fehlt. */
export const PRODUKTIONSSTATUS_LABEL = {
    OK: "Abgeschlossen",
    "PRODUCTION NOT PLANNED": "Nicht geplant",
    "PARTIALLY PRODUCED": "Teilweise produziert",
};

export function produktionsstatusLabel(status, statusText) {
    if (statusText) {
        return statusText;
    }
    return PRODUKTIONSSTATUS_LABEL[status] || status || "–";
}

/** Vergleicht zwei Kalenderwochen im Format "2026-39" numerisch (Jahr, dann Woche). */
export function compareKw(a, b) {
    const [jahrA, wocheA] = String(a ?? "").split("-").map(Number);
    const [jahrB, wocheB] = String(b ?? "").split("-").map(Number);
    if (jahrA !== jahrB) {
        return (jahrA || 0) - (jahrB || 0);
    }
    return (wocheA || 0) - (wocheB || 0);
}

/**
 * True, wenn die Fensterbreite unter der Grenze liegt - für Mobil-Anpassungen,
 * die sich nicht allein über CSS lösen lassen (z. B. Recharts-Beschriftungen,
 * Umschalten Knopfgruppe/Auswahlliste).
 */
export function useIstSchmal(grenzePx = 576) {
    const [schmal, setSchmal] = useState(() => (typeof window !== "undefined" ? window.innerWidth < grenzePx : false));

    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) {
            return undefined;
        }
        const mql = window.matchMedia(`(max-width: ${grenzePx - 1}px)`);
        const aktualisieren = () => setSchmal(mql.matches);
        aktualisieren();
        mql.addEventListener("change", aktualisieren);
        return () => mql.removeEventListener("change", aktualisieren);
    }, [grenzePx]);

    return schmal;
}
